import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import { Home, ShoppingBag, Wrench, CalendarDays, Phone, MessageCircle, MapPin, Clock, Lock } from "lucide-react";
import { useSettings } from "../../contexts/SettingsContext";
import { fileUrl } from "../../lib/api";
import OpenBadge from "../OpenBadge";

const NAV = [
  { to: "/", label: "Accueil" },
  { to: "/catalogue", label: "Catalogue" },
  { to: "/forfaits", label: "Forfaits" },
  { to: "/reparation", label: "Réparation" },
  { to: "/services", label: "Services" },
  { to: "/quiz", label: "Quiz" },
  { to: "/contact", label: "Contact" },
];

const MOBILE_NAV = [
  { to: "/", label: "Accueil", icon: Home },
  { to: "/catalogue", label: "Catalogue", icon: ShoppingBag },
  { to: "/reparation", label: "Devis", icon: Wrench },
  { to: "/rendez-vous", label: "RDV", icon: CalendarDays },
  { to: "/contact", label: "Contact", icon: Phone },
];

export default function PublicLayout() {
  const { settings, waLink, status } = useSettings();
  const location = useLocation();
  const storeName = settings?.store_name || "Oxyservices";
  const waNumber = (settings?.whatsapp_number || "").trim();

  return (
    <div className="flex min-h-screen flex-col bg-[#FAFAFA] pb-16 md:pb-0">
      <header className="sticky top-0 z-40 border-b border-amber-500/20 bg-white/90 backdrop-blur-md">
        <div className="container-oxy flex h-16 items-center justify-between gap-3">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5">
            {settings?.logo_image ? (
              <img src={fileUrl(settings.logo_image)} alt={storeName} data-testid="logo-image" className="h-10 w-10 rounded-xl object-contain shadow-md" />
            ) : (
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-ink font-display text-xl font-extrabold text-gold shadow-md">O</span>
            )}
            <span className={`leading-tight ${settings?.logo_image ? "hidden sm:block" : ""}`}>
              <span className="block font-display text-lg font-extrabold tracking-tight text-ink">{storeName}</span>
              <span className="hidden text-[10px] uppercase tracking-[0.18em] text-gold-dark sm:block">{settings?.tagline || "Téléphonie"}</span>
            </span>
          </Link>
          <nav className="hidden items-center gap-1 md:flex" data-testid="desktop-nav">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `rounded-full px-4 py-2 text-sm font-semibold transition-colors ${isActive ? "bg-ink text-gold" : "text-slate-700 hover:bg-slate-100"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <OpenBadge />
            <Link to="/reparation" data-testid="header-quote-btn" className="btn-gold !px-4 !py-2 text-sm">Devis gratuit</Link>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="mt-16 bg-ink text-slate-300">
        <div className="container-oxy grid gap-10 py-12 md:grid-cols-3">
          <div>
            <div className="flex items-center gap-2.5">
              {settings?.logo_image ? (
                <img src={fileUrl(settings.logo_image)} alt={storeName} className="h-10 w-10 rounded-xl object-contain" />
              ) : (
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gold font-display text-xl font-extrabold text-ink">O</span>
              )}
              <span className="font-display text-lg font-extrabold text-white">{storeName}</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-slate-400">
              Vente de téléphones & accessoires, réparation express, conseils d'experts.
            </p>
            {settings?.address && (
              <p className="mt-4 flex items-start gap-2 text-sm"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" />{settings.address}{settings.city ? `, ${settings.city}` : ""}</p>
            )}
            {settings?.phone && (
              <a href={`tel:${settings.phone}`} data-testid="footer-phone" className="mt-2 flex items-center gap-2 text-sm hover:text-gold">
                <Phone className="h-4 w-4 text-gold" />{settings.phone}
              </a>
            )}
          </div>
          <div>
            <h3 className="font-display text-sm font-bold uppercase tracking-widest text-gold">Navigation</h3>
            <ul className="mt-4 space-y-2 text-sm">
              {NAV.map((n) => (
                <li key={n.to}><Link to={n.to} className="hover:text-gold transition-colors">{n.label}</Link></li>
              ))}
              <li><Link to="/galerie" className="hover:text-gold transition-colors">Galerie</Link></li>
              <li><Link to="/forfaits" className="hover:text-gold transition-colors">Forfaits mobiles</Link></li>
              <li><Link to="/rendez-vous" className="hover:text-gold transition-colors">Rendez-vous</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="font-display text-sm font-bold uppercase tracking-widest text-gold">Horaires</h3>
            <ul className="mt-4 space-y-1.5 text-sm">
              {(settings?.hours || []).map((h) => (
                <li key={h.day} className="flex justify-between gap-4">
                  <span className="capitalize text-slate-400">{h.day}</span>
                  <span className="text-slate-200">
                    {h.open ? `${h.start} – ${h.end}${h.break_start ? ` (pause ${h.break_start}–${h.break_end})` : ""}` : "Fermé"}
                  </span>
                </li>
              ))}
            </ul>
            {status && (
              <p className="mt-3 flex items-center gap-2 text-xs text-slate-400">
                <Clock className="h-3.5 w-3.5 text-gold" />{status.label}
              </p>
            )}
          </div>
        </div>
        <div className="border-t border-white/10 py-5">
          <div className="container-oxy flex flex-col items-center justify-between gap-3 text-xs text-slate-500 sm:flex-row">
            <span>© {new Date().getFullYear()} {storeName}. Tous droits réservés.</span>
            <Link to="/admin/login" data-testid="footer-admin-link" className="inline-flex items-center gap-1.5 hover:text-gold transition-colors">
              <Lock className="h-3 w-3" /> Espace propriétaire
            </Link>
          </div>
        </div>
      </footer>

      {waNumber && (
        <a
          href={waLink("Bonjour Oxyservices, j'ai une question.")}
          target="_blank" rel="noopener noreferrer"
          data-testid="whatsapp-float-btn"
          aria-label="Nous contacter sur WhatsApp"
          className="fixed bottom-20 right-4 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-xl transition-transform duration-300 hover:scale-110 active:scale-95 md:bottom-6 md:right-6"
        >
          <MessageCircle className="h-7 w-7" fill="currentColor" />
        </a>
      )}

      <nav data-testid="mobile-nav" className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur-md md:hidden" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        <div className="grid grid-cols-5">
          {MOBILE_NAV.map((n) => {
            const active = n.to === "/" ? location.pathname === "/" : location.pathname.startsWith(n.to);
            const Icon = n.icon;
            return (
              <Link key={n.to} to={n.to} data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className={`flex flex-col items-center gap-1 py-2.5 text-[10px] font-semibold transition-colors ${active ? "text-gold-dark" : "text-slate-500"}`}>
                <Icon className={`h-5 w-5 ${active ? "text-gold" : ""}`} />
                {n.label}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
